import React, {
  Dispatch,
  SetStateAction,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FormGroup } from 'reactstrap';

import IUserGet from 'api/interfaces/users/IUserGet';
import { WRITE_CONTACTS } from 'services/enums/users/permissions';

import customerApi from 'api/contacts';
import maximize from 'assets/maximize.svg';
import minimize from 'assets/minimize.svg';
import LinkButton from 'components/layout/buttons/LinkButton';
import SearchDropdown from 'components/layout/forms/SearchDropdown';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';
import TrashIcon from 'components/layout/icons/TrashIcon';
import translations from 'services/translations/translations';
import { showDesigners } from 'components/offers/OfferSelectOptions';
import {
  CONTACT_ROLE,
  getChoiceList,
  toOptions,
  useOfferChoices,
} from 'services/offers/offerChoices';

import NewProjectContactModal, {
  NEW_COMPANY,
  NEW_PERSON,
  NEW_PERSON_CHOICE,
} from './NewProjectContactModal';

import 'style/offers/ProjectContacts.css';

// The tasks are a list the admins manage (CONTACT_ROLE): those of the code and their own

// The tasks of a row as the options of a select; a customer may also have none
export const useRoleOptions = (
  withEmpty = true
): ((current?: string | null) => JSX.Element[]) => {
  const choices = useOfferChoices();
  const { i18n } = useTranslation();

  return (current) =>
    toOptions(
      getChoiceList(choices, CONTACT_ROLE, current, i18n.language),
      withEmpty
    );
};

export interface IProjectContactRow {
  key: number;
  office: { label: string; value: string } | null;
  personId: string;
  // The contact persons of the company, null until loaded
  persons: any[] | null;
  role: string;
}

const loadPersons = async (officeId: string): Promise<any[]> => {
  try {
    const contact = await customerApi.getContact(officeId, {
      ownOrganizationUnit: true,
    });
    return contact.contactPersons ?? [];
  } catch (error) {
    return [];
  }
};

// Builds the rows from the saved contacts of the project (with their roles). The contact
// persons are loaded by the rows themselves, so the rows show at once.
export const getProjectContactRows = (
  contacts: {
    contact: { id: number; companyName: string };
    contactPerson: { id: number } | null;
    type?: string;
  }[]
): IProjectContactRow[] =>
  contacts.map((projectContact, index) => ({
    key: index + 1,
    office: {
      label: projectContact.contact.companyName,
      value: projectContact.contact.id.toString(),
    },
    personId: projectContact.contactPerson?.id?.toString() ?? '',
    persons: null,
    role: projectContact.type || 'designer',
  }));

const emptyRow = (key: number): IProjectContactRow => ({
  key,
  office: null,
  personId: '',
  persons: [],
  role: 'designer',
});

// The rows with a company, as saved with the offer
export const getProjectContactsPost = (
  rows: IProjectContactRow[]
): { contactId: number; contactPersonId?: number; type: string }[] =>
  rows
    .filter((row) => row.office !== null && row.office.value !== '')
    .map((row) => ({
      contactId: parseInt(row.office?.value ?? '', 10),
      contactPersonId:
        row.personId === '' ? undefined : parseInt(row.personId, 10),
      type: row.role,
    }));

/**
 * The contacts of the project besides the customers, e.g. the architects, the main
 * contractor and the inspector: rows of a role, a company and a contact person. The
 * section is opened and closed from its title with the plus and minus icons.
 */
const ProjectContacts = ({
  leadingRow,
  locked,
  rows,
  searchOffice,
  setRows,
}: {
  // Of a sales opportunity: the customer as the first row, all contacts in one list
  leadingRow?: JSX.Element;
  locked: boolean;
  rows: IProjectContactRow[];
  searchOffice: (inputValue: string) => Promise<any>;
  setRows: Dispatch<SetStateAction<IProjectContactRow[]>>;
}): JSX.Element => {
  const { t } = useTranslation([translations.offers, translations.common]);
  const roleOptions = useRoleOptions(false);
  const [isOpen, setIsOpen] = useState(true);
  // A new company or contact person being created on a row
  const [newContact, setNewContact] = useState<null | {
    mode: string;
    rowKey: number;
  }>(null);
  const newContactRow =
    newContact === null
      ? undefined
      : rows.find((row) => row.key === newContact.rowKey);

  // Companies and persons can be created by the users who can edit the contacts
  const currentUser = useSelector(
    (state: { users: { current: IUserGet | null } }) => state.users.current
  );
  const canCreate =
    !locked &&
    (currentUser?.permissions?.some(
      (permission) => permission.type === WRITE_CONTACTS
    ) ??
      false);
  // The companies whose contact persons are being loaded, by row: "key:company id"
  const loading = useRef(new Set<string>());

  // The contact persons of each company are loaded in the background. The rows are always
  // updated from their latest state, and only while the row still has the same company, so
  // a slow answer never overwrites what the user has changed meanwhile.
  useEffect(() => {
    rows
      .filter((row) => row.persons === null && row.office?.value)
      .forEach((row) => {
        const officeId = row.office?.value ?? '';
        const loadKey = `${row.key}:${officeId}`;

        if (loading.current.has(loadKey)) {
          return;
        }

        loading.current.add(loadKey);
        loadPersons(officeId).then((persons) => {
          loading.current.delete(loadKey);
          setRows((current) =>
            current.map((currentRow) =>
              currentRow.key === row.key &&
              currentRow.office?.value === officeId
                ? { ...currentRow, persons }
                : currentRow
            )
          );
        });
      });
  }, [rows, setRows]);

  // The ready empty row becomes a real one when something is chosen on it
  const updateRow = (key: number, changes: Partial<IProjectContactRow>) =>
    setRows((current) =>
      current.some((row) => row.key === key)
        ? current.map((row) => (row.key === key ? { ...row, ...changes } : row))
        : [...current, { ...emptyRow(key), ...changes }]
    );

  const changeOffice = (
    key: number,
    office: { label: string; value: string } | null
  ) =>
    updateRow(key, {
      office,
      personId: '',
      persons: office !== null && office.value !== '' ? null : [],
    });

  // Below the ready empty row (which becomes a real one) when there are no rows yet
  const addRow = () => {
    setRows((current) => [
      ...(current.length === 0 && leadingRow === undefined
        ? [emptyRow(1)]
        : current),
      {
        ...emptyRow(Math.max(1, ...current.map((row) => row.key)) + 1),
        role: 'other',
      },
    ]);
    setIsOpen(true);
  };

  const removeRow = (key: number) =>
    setRows((current) => current.filter((row) => row.key !== key));

  // A company or a person created on a row is chosen on it
  const contactCreated = (created: { id: number; label: string }) => {
    if (newContact === null) {
      return;
    }

    if (newContact.mode === NEW_COMPANY) {
      changeOffice(newContact.rowKey, {
        label: created.label,
        value: created.id.toString(),
      });
    } else {
      // The persons of the company are loaded again with the new one
      updateRow(newContact.rowKey, {
        personId: created.id.toString(),
        persons: null,
      });
    }

    setNewContact(null);
  };

  const toggle = () => setIsOpen(!isOpen);

  // One empty row is always ready to be filled in (the customer row when it leads)
  const shownRows =
    rows.length === 0 && !locked && leadingRow === undefined
      ? [emptyRow(1)]
      : rows;

  // A new company goes to the last row while it has no company, otherwise to a new row
  // after it (made as "+ Lisää yhteystieto" makes one): never in place of a chosen company
  const addNewCompany = () => {
    const last = shownRows[shownRows.length - 1];
    if (last !== undefined && !last.office?.value) {
      setNewContact({ mode: NEW_COMPANY, rowKey: last.key });
      return;
    }

    const key = Math.max(1, ...rows.map((row) => row.key)) + 1;
    addRow();
    setNewContact({ mode: NEW_COMPANY, rowKey: key });
  };

  return (
    <div className="project-contacts">
      <div
        className="project-contacts-title"
        onClick={toggle}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            toggle();
          }
        }}
        role="button"
        tabIndex={0}
      >
        <img
          src={isOpen ? minimize : maximize}
          alt={t(`${translations.offers}:projectContacts.Title`)}
          className="collapse-icon"
        />
        {t(`${translations.offers}:projectContacts.Title`)}
        {isOpen || rows.length === 0 ? null : (
          <span className="project-contacts-count">
            ({rows.length + (leadingRow === undefined ? 0 : 1)})
          </span>
        )}
      </div>
      {isOpen ? (
        <>
          {/* Company, contact person and task side by side, as the customers; an empty
              row is ready to be filled */}
          <div className="project-contacts-grid project-contacts-header">
            <span>{t(`${translations.offers}:projectContacts.Company`)}</span>
            <span>{t(`${translations.offers}:projectContacts.Person`)}</span>
            <span>{t(`${translations.offers}:projectContacts.Role`)}</span>
            <span />
          </div>
          {leadingRow}
          {shownRows.map((row) => (
            <div className="project-contacts-grid" key={row.key}>
              <FormGroup>
                <SearchDropdown
                  value={row.office}
                  placeholder={t(`${translations.common}:Search`)}
                  className="text-field search-field"
                  name={`project-contact-office-${row.key}`}
                  onChange={(office: any) => changeOffice(row.key, office)}
                  loadOptions={searchOffice}
                  disabled={locked}
                />
              </FormGroup>
              <div>
                <SelectInputFieldControlled
                  value={row.personId}
                  id={`project-contact-person-${row.key}`}
                  name={`project-contact-person-${row.key}`}
                  onChange={(event: any) => {
                    const { value } = event.target;
                    // "+ Uusi henkilö" of the list: the window, the person stays as it was
                    if (value === NEW_PERSON_CHOICE) {
                      setNewContact({ mode: NEW_PERSON, rowKey: row.key });
                      return;
                    }
                    updateRow(row.key, { personId: value });
                  }}
                  options={[
                    ...showDesigners(row.persons ?? []),
                    ...(canCreate &&
                    row.office !== null &&
                    row.office.value !== ''
                      ? [
                          <option
                            key={NEW_PERSON_CHOICE}
                            value={NEW_PERSON_CHOICE}
                          >
                            {`+ ${t(
                              `${translations.offers}:projectContacts.New person`
                            )}…`}
                          </option>,
                        ]
                      : []),
                  ]}
                  disabled={locked || row.office === null}
                />
              </div>
              <SelectInputFieldControlled
                value={row.role}
                id={`project-contact-role-${row.key}`}
                name={`project-contact-role-${row.key}`}
                onChange={(event: any) =>
                  updateRow(row.key, { role: event.target.value })
                }
                options={roleOptions(row.role)}
                disabled={locked}
              />
              <div>
                {locked ||
                !rows.some((saved) => saved.key === row.key) ? null : (
                  <TrashIcon
                    style={{ marginTop: '0.5rem' }}
                    onClick={() => removeRow(row.key)}
                  />
                )}
              </div>
            </div>
          ))}
          {/* Below the rows, not between them: a contact, then a new company under it */}
          {locked ? null : (
            <div className="procurement-contacts-actions">
              <LinkButton onClick={addRow}>
                {`+ ${t(`${translations.offers}:projectContacts.Add`)}`}
              </LinkButton>
              {canCreate ? (
                <LinkButton onClick={addNewCompany}>
                  {`+ ${t(
                    `${translations.offers}:projectContacts.New company`
                  )}`}
                </LinkButton>
              ) : null}
            </div>
          )}
        </>
      ) : null}
      <NewProjectContactModal
        company={newContactRow?.office ?? null}
        mode={newContact?.mode ?? null}
        onCreated={contactCreated}
        organizationUnitId={currentUser?.organizationUnit?.id}
        role={newContactRow?.role ?? 'other'}
        toggle={() => setNewContact(null)}
      />
    </div>
  );
};

export default ProjectContacts;
